import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography
} from '@material-ui/core';
import { Map } from 'immutable';
import * as React from 'react';
import { connect } from 'react-redux';

import { updatePerson } from '../actions';
import { List, Person, PersonDetails, RequestErrors, State } from '../state';

interface OwnProps {
    personId: string;
}

interface ConnectedProps {
    pendingRequests: Map<string, RequestErrors>;
    persons: List<PersonDetails>;
}

interface ConnectedDispatch {
    updatePerson: (id: string, updates: Partial<Person>) => void;
}

type CandidateRemindersProps = ConnectedProps & ConnectedDispatch & OwnProps;

interface PersonNameState {
    first: string;
    full: string;
    last: string;
    middle: string;
}

const PersonNameEditComponent: React.FC<CandidateRemindersProps> = (props) => {
    const { personId, pendingRequests, persons } = props;
    const personDetail = persons.list.get(personId);
    const { first, full, last } = personDetail.person.name;
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [name, setName] = React.useState<PersonNameState>({ first: '', middle: '', last: '', full: '' });

    const handleDialogOpen = () => {
        const middle = (full.replace(new RegExp(`^${first}`), '').replace(new RegExp(`${last}$`), '') ?? '').trim();
        setName({ first, full, last, middle });
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const setNameText = (field: keyof PersonNameState) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const newName = { ...name, [field]: value };
        const newFull = `${newName.first} ${newName.middle} ${newName.last}`.trim().replace(/\s+/g, ' ');
        newName.full = newFull;
        setName(newName);
    };

    const handleUpdateName = () => {
        const updatedName = {
            edited: true,
            first: name.first.trim().replace(/\s+/g, ' '),
            full: name.full,
            last: name.last.trim().replace(/\s+/g, ' ')
        };
        props.updatePerson(personId, { name: updatedName });
        handleDialogClose();
    };

    const inProgress = pendingRequests.has(`person-update-request-${personId}`);
    let dialog;
    if (dialogOpen) {
        const disabled = personDetail.person.name.full === name.full || !name.first || !name.last;
        dialog = (
            <Dialog open={true} onClose={handleDialogClose}>
                <DialogTitle>
                    <Typography variant="h4" component="div">
                        Update Person Name
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <div className="person-name-edit">
                        <TextField
                            value={name.first}
                            label="First"
                            onChange={setNameText('first')}
                            name="person-first-name-text-field"
                            fullWidth={true}
                            disabled={inProgress}
                            autoComplete="off"
                            className="name-field"
                        />
                        <TextField
                            value={name.middle}
                            label="Middle"
                            onChange={setNameText('middle')}
                            name="person-middle-name-text-field"
                            fullWidth={true}
                            disabled={inProgress}
                            autoComplete="off"
                            className="name-field"
                        />
                        <TextField
                            value={name.last}
                            label="Last"
                            onChange={setNameText('last')}
                            name="person-last-name-text-field"
                            fullWidth={true}
                            disabled={inProgress}
                            autoComplete="off"
                            className="name-field"
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={handleDialogClose} key="cancel" variant="text">
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleUpdateName} key="save" disabled={disabled} variant="text">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <div className="header-candidate-edit-name">
            <IconButton style={{ color: 'white' }} onClick={handleDialogOpen}>
                <i className="material-icons">edit</i>
            </IconButton>
            {dialog}
        </div>
    );
};

const mapStateToProps = (state: State): ConnectedProps => ({
    pendingRequests: state.pendingRequests,
    persons: state.personsDetails
});

const mapDispatchToProps: { [action in keyof ConnectedDispatch]: ConnectedDispatch[action] } = {
    updatePerson
};

export const PersonNameEdit = connect<ConnectedProps, ConnectedDispatch, OwnProps>(
    mapStateToProps,
    mapDispatchToProps
)(PersonNameEditComponent);
